import React, { forwardRef, useState, useRef, useCallback } from 'react';
import T from 'prop-types';

import Animation from './Animation';
import { useCombinedRef } from '../hooks';

const property = {
  vertical: 'height',
  horizontal: 'width',
};

const scrollProperty = {
  vertical: 'scrollHeight',
  horizontal: 'scrollWidth',
};

// eslint-disable-next-line prefer-arrow-callback
const CollapsibleSlot = forwardRef(function CollapsibleSlot(
  {
    show,
    children,
    initialSize = 0,
    direction = 'vertical',
    animationType,
    unmountOnExit,
    id,
    className,
    onClick,
  },
  ref
) {
  const slotRef = useRef();
  const [slotSize, setSlotSize] = useState(show ? undefined : initialSize);

  const setCurrentSize = useCallback(() => {
    setSlotSize(slotRef.current[scrollProperty[direction]]);
  }, [direction]);

  const resetSize = useCallback(() => {
    setSlotSize(initialSize);
  }, [initialSize]);

  const unsetSize = useCallback(() => {
    setSlotSize();
  }, []);

  return (
    <Animation
      show={show}
      animationType={animationType}
      unmountOnExit={unmountOnExit}
      onEnter={setCurrentSize}
      onEntered={unsetSize}
      onExit={setCurrentSize}
      onExiting={resetSize}
    >
      <div
        ref={useCombinedRef(ref, slotRef)}
        id={id}
        className={className}
        style={{ [property[direction]]: slotSize }}
        onClick={onClick}
      >
        {children}
      </div>
    </Animation>
  );
});

CollapsibleSlot.propTypes = {
  show: T.bool.isRequired,
  children: T.node.isRequired,
  initialSize: T.number,
  direction: T.oneOf(['vertical', 'horizontal']),
  animationType: T.string,
  unmountOnExit: T.bool,
  id: T.string,
  className: T.string,
  onClick: T.func,
};

export default CollapsibleSlot;
